import { formatAlertSince } from "./alertPresentationUtils";
import { formatLocalDate } from "./datetimeUtils";

function stripTime(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function parseInjuryDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function getInjuryEndDate(injury) {
  const start = parseInjuryDate(injury?.injury_date);
  const weeks = Number(injury?.duration_weeks);
  if (!start || !Number.isFinite(weeks) || weeks <= 0) return null;

  const end = new Date(start);
  end.setDate(end.getDate() + weeks * 7);
  return end;
}

export function isInjuryActive(injury, now = new Date()) {
  const start = parseInjuryDate(injury?.injury_date);
  const end = getInjuryEndDate(injury);
  if (!start || !end) return false;

  const today = stripTime(now);
  return today >= stripTime(start) && today <= stripTime(end);
}

export function getActiveInjuries(injuries = [], now = new Date()) {
  return (injuries || [])
    .filter((injury) => isInjuryActive(injury, now))
    .sort((a, b) => {
      const dateA = parseInjuryDate(a?.injury_date)?.getTime() || 0;
      const dateB = parseInjuryDate(b?.injury_date)?.getTime() || 0;
      return dateB - dateA;
    });
}

export function getInjuryProgress(injury, now = new Date()) {
  const start = parseInjuryDate(injury?.injury_date);
  const end = getInjuryEndDate(injury);
  const durationWeeks = Number(injury?.duration_weeks);

  if (!start || !end || !Number.isFinite(durationWeeks) || durationWeeks <= 0) {
    return { weeksElapsed: null, weeksRemaining: null, progressRatio: null };
  }

  const today = stripTime(now);
  const elapsedDays = Math.max(0, Math.floor((today - stripTime(start)) / 86400000));
  const totalDays = Math.max(1, durationWeeks * 7);
  const remainingDays = Math.max(0, Math.ceil((stripTime(end) - today) / 86400000));

  return {
    weeksElapsed: Math.floor(elapsedDays / 7),
    weeksRemaining: Math.ceil(remainingDays / 7),
    progressRatio: Math.min(1, elapsedDays / totalDays),
  };
}

export function formatInjurySide(side, t) {
  const key = String(side || "").trim().toLowerCase();
  if (!key) return null;
  if (["left", "gauche", "l"].includes(key)) {
    return t("bodymap.left", { defaultValue: "Left" });
  }
  if (["right", "droite", "r"].includes(key)) {
    return t("bodymap.right", { defaultValue: "Right" });
  }
  if (["both", "bilateral", "bilat", "double"].includes(key)) {
    return t("player_risk_alert.bilateral", { defaultValue: "Bilateral" });
  }
  return side;
}

export function formatInjurySince(injury, locale) {
  return formatAlertSince(injury?.injury_date, locale);
}

export function formatInjuryDateLabel(injury) {
  return formatLocalDate(injury?.injury_date) || null;
}

export function resolveInjuryStatusKey(injury, now = new Date()) {
  const { weeksRemaining } = getInjuryProgress(injury, now);
  if (weeksRemaining === null) return "active";
  if (weeksRemaining <= 0) return "ending";
  if (weeksRemaining <= 1) return "returning_soon";
  return "active";
}
